import { useState } from "react";

interface CustomerInfoCardProps {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  zip: string;
  shippingMethodName: string;
  trackingNumber: string | null;
  shippingCompany: string | null;
  onSave: (data: Record<string, string | null>) => Promise<unknown>;
  isSaving?: boolean;
}

export default function CustomerInfoCard({
  firstName: initialFirstName,
  lastName: initialLastName,
  email: initialEmail,
  phone: initialPhone,
  address: initialAddress,
  city: initialCity,
  zip: initialZip,
  shippingMethodName,
  trackingNumber: initialTrackingNumber,
  shippingCompany: initialShippingCompany,
  onSave,
  isSaving,
}: CustomerInfoCardProps) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    firstName: initialFirstName,
    lastName: initialLastName,
    email: initialEmail,
    phone: initialPhone,
    address: initialAddress,
    city: initialCity,
    zip: initialZip,
    trackingNumber: initialTrackingNumber ?? "",
    shippingCompany: initialShippingCompany ?? "",
  });

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  function handleCancel() {
    setForm({
      firstName: initialFirstName,
      lastName: initialLastName,
      email: initialEmail,
      phone: initialPhone,
      address: initialAddress,
      city: initialCity,
      zip: initialZip,
      trackingNumber: initialTrackingNumber ?? "",
      shippingCompany: initialShippingCompany ?? "",
    });
    setEditing(false);
  }

  async function handleSave() {
    await onSave({
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      phone: form.phone,
      address: form.address,
      city: form.city,
      zip: form.zip,
      trackingNumber: form.trackingNumber === "" ? null : form.trackingNumber,
      shippingCompany: form.shippingCompany === "" ? null : form.shippingCompany,
    });
    setEditing(false);
  }

  return (
    <div className="bg-white rounded-xl border border-border/60 overflow-hidden">
      <div className="px-5 py-3 border-b border-border/40 flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Kupac</h3>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
        >
          Uredi
        </button>
      </div>
      <div className="p-5 space-y-2 text-sm">
        {editing ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-muted-foreground/70 block mb-0.5">Ime</label>
                <input name="firstName" value={form.firstName} onChange={handleChange} className="w-full border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-foreground" />
              </div>
              <div>
                <label className="text-[11px] text-muted-foreground/70 block mb-0.5">Prezime</label>
                <input name="lastName" value={form.lastName} onChange={handleChange} className="w-full border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-foreground" />
              </div>
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground/70 block mb-0.5">Email</label>
              <input name="email" value={form.email} onChange={handleChange} className="w-full border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-foreground" />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground/70 block mb-0.5">Telefon</label>
              <input name="phone" value={form.phone} onChange={handleChange} className="w-full border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-foreground" />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground/70 block mb-0.5">Adresa</label>
              <input name="address" value={form.address} onChange={handleChange} className="w-full border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-foreground" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-muted-foreground/70 block mb-0.5">Grad</label>
                <input name="city" value={form.city} onChange={handleChange} className="w-full border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-foreground" />
              </div>
              <div>
                <label className="text-[11px] text-muted-foreground/70 block mb-0.5">Poštanski broj</label>
                <input name="zip" value={form.zip} onChange={handleChange} className="w-full border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-foreground" />
              </div>
            </div>
            <div className="pt-2 border-t border-border/40 mt-3">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">Tracking</p>
              <div className="space-y-2">
                <div>
                  <label className="text-[11px] text-muted-foreground/70 block mb-0.5">Broj za praćenje</label>
                  <input name="trackingNumber" value={form.trackingNumber} onChange={handleChange} className="w-full border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-foreground font-mono" placeholder="npr. XE-12345" />
                </div>
                <div>
                  <label className="text-[11px] text-muted-foreground/70 block mb-0.5">Kurirska služba</label>
                  <input name="shippingCompany" value={form.shippingCompany} onChange={handleChange} className="w-full border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-foreground" placeholder="npr. X Express" />
                </div>
              </div>
            </div>
            <div className="flex gap-2 pt-3 border-t border-border/40 mt-3">
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className="flex-1 bg-foreground text-white text-sm rounded-lg py-2 font-medium hover:opacity-90 transition-opacity disabled:opacity-50 cursor-pointer"
              >
                {isSaving ? "Spremanje..." : "Spremi"}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={isSaving}
                className="flex-1 border border-border text-sm rounded-lg py-2 font-medium hover:bg-muted/50 transition-colors cursor-pointer"
              >
                Odustani
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="font-medium">{initialFirstName} {initialLastName}</p>
            <p className="text-muted-foreground">{initialEmail}</p>
            <p className="text-muted-foreground">{initialPhone}</p>
            <div className="pt-2 border-t border-border/40 mt-3">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">Adresa dostave</p>
              <p>{initialAddress}</p>
              <p>{initialCity} {initialZip}</p>
            </div>
            <div className="pt-2 border-t border-border/40 mt-3">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">Način dostave</p>
              <p>{shippingMethodName}</p>
            </div>
            <div className="pt-2 border-t border-border/40 mt-3">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">Plaćanje</p>
              <p>Pouzeće (gotovina)</p>
            </div>
            <div className="pt-2 border-t border-border/40 mt-3">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">Tracking</p>
              {initialTrackingNumber ? (
                <>
                  <p className="font-mono text-sm">{initialTrackingNumber}</p>
                  {initialShippingCompany && (
                    <p className="text-xs text-muted-foreground mt-0.5">{initialShippingCompany}</p>
                  )}
                </>
              ) : (
                <p className="text-muted-foreground/50 italic">Nije dodijeljen</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
